import React, { useState, useEffect } from 'react';
import { apiClient } from '../config/api';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingDown, BarChart3, Award } from 'lucide-react';

const PerformanceChart = () => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedActivist, setSelectedActivist] = useState('all');
  const [error, setError] = useState(null);
  const [returnPeriod, setReturnPeriod] = useState('1m'); // '1m' or '1y'

  // Color palette for different activists
  const activistColors = {
    'Muddy Waters Research': '#EF4444',
    'Hindenburg Research': '#F59E0B',
    'Blue Orca Capital': '#3B82F6',
    'J Capital Research': '#8B5CF6',
    'Grizzly Research': '#10B981',
    'Fuzzy Panda Research': '#EC4899',
    'Spruce Point Capital Management': '#6366F1',
    'Iceberg Research': '#14B8A6',
    'Bonitas Research': '#F97316',
    'Gotham City Research': '#84CC16',
    'Anonymous Analytics': '#6B7280',
    'Culper Research': '#DC2626',
    'White Diamond Research': '#0891B2',
    'Kerrisdale Capital': '#7C3AED',
    'Citron Research': '#EAB308',
  };

  useEffect(() => {
    fetchChartData();
  }, []);

  const fetchChartData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/performance/all-reports-chart');
      setChartData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching chart data:', err);
      setError('Failed to load chart data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  // Format from SHORT SELLER perspective (already inverted in data)
  const formatPercent = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '';
    return `${value.toFixed(0)}%`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const displayRet = data.display_return; // Already inverted in data processing
      
      // Determine if we're showing 1-year or current return
      const isUsing1YearData = returnPeriod === '1y' && !isNaN(parseFloat(data.return_1y_pct));
      const periodLabel = returnPeriod === '1m' 
        ? '1-Month' 
        : (isUsing1YearData ? '1-Year' : 'Current');
      
      return (
        <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3 max-w-xs">
          <p className="font-bold text-gray-900">{data.target_company}</p>
          <p className="text-sm text-gray-600">{data.ticker}</p>
          <p className="text-xs text-gray-500 mb-2">{data.short_seller}</p>
          <div className="space-y-1 text-sm">
            <p>
              <span className="text-gray-600">{periodLabel} Short P&L:</span>{' '}
              <span className={displayRet > 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                {!isNaN(displayRet) ? (displayRet >= 0 ? '+' : '') + displayRet.toFixed(2) : 'N/A'}%
              </span>
              {returnPeriod === '1y' && !isUsing1YearData && (
                <span className="text-xs text-gray-500 block">{'<1 year old, showing current'}</span>
              )}
            </p>
            <p>
              <span className="text-gray-600">Date:</span> {formatDate(data.publication_date)}
            </p>
            {data.sector && <p className="text-xs text-gray-500">{data.sector}</p>}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600">Loading visualization data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="flex justify-center items-center h-64">
          <div className="text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  if (!chartData || !chartData.reports || !Array.isArray(chartData.reports) || !Array.isArray(chartData.activistStats)) {
    return (
      <div className="card">
        <div className="text-center py-8 text-gray-600">
          {chartData ? 'Invalid data format received from API' : 'No data available'}
        </div>
      </div>
    );
  }

  // Parse numeric values from strings and filter reports
  // Show returns from SHORT SELLER perspective (inverted from stock price change)
  const allFilteredReports = (selectedActivist === 'all' 
    ? chartData.reports 
    : chartData.reports.filter(r => r.short_seller === selectedActivist)
  ).map(report => {
    const return_1m = parseFloat(report.return_1m_pct);
    const return_1y = parseFloat(report.return_1y_pct);
    const total_return = parseFloat(report.total_return_pct);
    
    // For 1-year view: use 1-year return if available, otherwise fall back to total return for reports < 1 year old
    // For 1-month view: only use 1-month return
    let displayReturn;
    if (returnPeriod === '1m') {
      displayReturn = return_1m;
    } else {
      // For 1-year: prefer 1-year data, but use current return if 1-year not available yet
      displayReturn = !isNaN(return_1y) ? return_1y : total_return;
    }
    
    // INVERT for short seller perspective: negative stock return = positive short profit
    displayReturn = -displayReturn;
    
    return {
      ...report,
      total_return_pct: total_return || 0,
      return_1m_pct: return_1m,
      return_1y_pct: return_1y,
      display_return: displayReturn, // Now inverted
      publication_date_formatted: new Date(report.publication_date).getTime(), // Convert to timestamp for proper sorting
    };
  }).filter(report => report.display_return !== null && !isNaN(report.display_return)); // Only show reports with valid return data

  // Fixed Y-axis domain from -200% to +100% (SHORT SELLER perspective)
  // Negative = loss (stock went up), Positive = profit (stock went down)
  const yAxisDomain = [-200, 100];
  
  // Filter out extreme outliers outside the display range for better visualization
  const filteredReports = allFilteredReports.filter(report => 
    report.display_return >= yAxisDomain[0] && report.display_return <= yAxisDomain[1]
  );
  
  // Count excluded outliers
  const excludedCount = allFilteredReports.length - filteredReports.length;

  // Get top 10 activists by success rate for the legend
  const topActivists = Array.isArray(chartData.activistStats) ? chartData.activistStats.slice(0, 10) : [];
  
  // Get color for an activist
  const getActivistColor = (activist, index) => {
    return activistColors[activist] || `hsl(${(index * 137) % 360}, 70%, 50%)`;
  };

  return (
    <div className="space-y-6">
      {/* Dropdown Filter for All Activists */}
      <div className="card">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filter by Activist (Select from all {chartData.activistStats.length} activists):
        </label>
        <select
          value={selectedActivist}
          onChange={(e) => setSelectedActivist(e.target.value)}
          className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
        >
          <option value="all">All Activists ({chartData.activistStats.length} total)</option>
          {chartData.activistStats.map((activist) => (
            <option key={activist.short_seller} value={activist.short_seller}>
              {activist.short_seller} ({activist.total_reports} reports, {activist.success_rate}% success)
            </option>
          ))}
        </select>
      </div>

      {/* Top Activists Success Rate */}
      <div className="card bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
        <div className="flex items-center space-x-3 mb-4">
          <Award className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-bold text-gray-900">Top 10 Activists by Success Rate (Quick Filter)</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {topActivists.map((activist, index) => (
            <div 
              key={activist.short_seller}
              onClick={() => setSelectedActivist(selectedActivist === activist.short_seller ? 'all' : activist.short_seller)}
              className={`cursor-pointer p-3 rounded-lg border-2 transition-all ${
                selectedActivist === activist.short_seller 
                  ? 'bg-blue-100 border-blue-500 shadow-md scale-105' 
                  : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow'
              }`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: getActivistColor(activist.short_seller, index) }}
                />
                <span className="text-xs font-semibold text-gray-600">#{index + 1}</span>
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">
                {activist.short_seller}
              </p>
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Success:</span>
                  <span className="text-sm font-bold text-green-700">{activist.success_rate}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Reports:</span>
                  <span className="text-sm font-semibold text-gray-700">{activist.total_reports}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Median:</span>
                  <span className={`text-sm font-semibold ${parseFloat(activist.median_return) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {activist.median_return}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {selectedActivist !== 'all' && (
          <div className="mt-4 p-3 bg-blue-100 rounded-lg">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">Filtered:</span> Showing only {selectedActivist} reports. 
              <button 
                onClick={() => setSelectedActivist('all')}
                className="ml-2 underline hover:text-blue-700"
              >
                Clear filter
              </button>
            </p>
          </div>
        )}
      </div>

      {/* Scatter Plot */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Short Seller P&L Over Time ({returnPeriod === '1m' ? '1-Month' : '1-Year'})
            </h2>
            <p className="text-sm text-gray-600">
              Showing {filteredReports.length} reports with {returnPeriod === '1m' ? '1-month' : '1-year'} profit/loss data
              {selectedActivist !== 'all' ? ` from ${selectedActivist}` : ' from all activists'}
              {excludedCount > 0 && (
                <span className="text-xs text-gray-500"> ({excludedCount} extreme outlier{excludedCount !== 1 ? 's' : ''} excluded)</span>
              )}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setReturnPeriod('1m')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  returnPeriod === '1m' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                1 Month
              </button>
              <button
                onClick={() => setReturnPeriod('1y')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  returnPeriod === '1y' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                1 Year
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              <span className="text-sm text-gray-600">Lower = Better</span>
            </div>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={600}>
          <ScatterChart
            margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              type="number"
              dataKey="publication_date_formatted" 
              name="Publication Date"
              domain={['dataMin', 'dataMax']}
              tickFormatter={formatTimestamp}
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fontSize: 12 }}
              label={{ 
                value: 'Publication Date', 
                position: 'bottom',
                offset: 40,
                style: { fontSize: 14, fontWeight: 'bold' }
              }}
            />
            <YAxis 
              dataKey="display_return" 
              name="Return %"
              domain={yAxisDomain}
              tickFormatter={formatPercent}
              tick={{ fontSize: 12 }}
              label={{ 
                value: `${returnPeriod === '1m' ? '1-Month' : '1-Year'} Return (%)`, 
                angle: -90, 
                position: 'insideLeft',
                style: { fontSize: 14, fontWeight: 'bold' }
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="#6B7280" strokeWidth={2} strokeDasharray="5 5" />
            <ReferenceLine y={-50} stroke="#EF4444" strokeWidth={1} strokeDasharray="3 3" label={{ value: '-50%', position: 'right', fill: '#EF4444', fontSize: 11 }} />
            
            {selectedActivist === 'all' ? (
              // Show all activists with different colors
              topActivists.map((activist, index) => {
                const activistReports = filteredReports.filter(r => r.short_seller === activist.short_seller);
                return (
                  <Scatter
                    key={activist.short_seller}
                    name={activist.short_seller}
                    data={activistReports}
                    fill={getActivistColor(activist.short_seller, index)}
                    opacity={0.7}
                  />
                );
              })
            ) : (
              // Show selected activist
              <Scatter
                name={selectedActivist}
                data={filteredReports}
                fill={getActivistColor(selectedActivist, 0)}
                opacity={0.8}
              />
            )}
          </ScatterChart>
        </ResponsiveContainer>

        <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-3">
            <BarChart3 className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-gray-900">How to read this chart (Short Seller P&L):</p>
              <ul className="text-sm text-gray-600 mt-2 space-y-1 list-disc list-inside">
                <li>
                  <span className="font-semibold">Returns shown:</span> Short seller profit/loss {returnPeriod === '1m' ? '1 month' : '1 year'} after report
                  {returnPeriod === '1m' && ' (not cumulative to today)'}
                  {returnPeriod === '1y' && ' (for reports <1 year old, shows current P&L)'}
                </li>
                <li><span className="font-semibold">Y-axis ABOVE 0:</span> Profitable short (stock declined) ✅ 🟢</li>
                <li><span className="font-semibold">Y-axis BELOW 0:</span> Losing short (stock increased) ❌ 🔴</li>
                <li><span className="font-semibold">Example:</span> +50% = stock fell 50%, short seller made 50% profit</li>
                <li><span className="font-semibold">Toggle:</span> Switch between 1-month and 1-year returns using the buttons above</li>
                <li><span className="font-semibold">Click activist cards</span> above to filter by specific fund</li>
                <li><span className="font-semibold">Hover over dots</span> to see detailed report information</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceChart;

